/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { firestore } from '@/utils/firebase';
import { AI_WORKFLOWS_COLLECTION } from '@/features/aiWorkflow/constants';

const useGetAIWorkflows = ({ dealId = '' }) => {
	const [workflows, setWorkflows] = useState([]);

	const [isLoading, setIsLoading] = useState(true);
	const [isFetching, setIsFetching] = useState(true);
	const [isUninitialized, setIsUninitialized] = useState(true);
	const [isError, setIsError] = useState(false);

	const isSuccess = useMemo(
		() => !isError && !isUninitialized && !isLoading && !isFetching,
		[isError, isLoading, isUninitialized, isFetching],
	);

	const reset = useCallback(() => {
		setWorkflows([]);
		setIsLoading(false);
		setIsFetching(false);
		setIsUninitialized(true);
		setIsError(false);
	}, []);

	useEffect(() => {
		if (!dealId) {
			reset();
			return undefined;
		}
		setIsUninitialized(false);
		const collectionRef = collection(firestore, AI_WORKFLOWS_COLLECTION);
		const q = query(collectionRef, where('dealId', '==', dealId));

		const unsubscribe = onSnapshot(q, (snapshot) => {
			try {
				setIsFetching(true);
				setWorkflows((prev) => {
					let updated = [...prev];

					snapshot.docChanges().forEach((change) => {
						const doc = change.doc.data();
						const id = change.doc.id;

						const feature = {
							id,
							...doc,
							dateCreated: doc.dateCreated.toDate(),
							dateCreatedMillis: doc.dateCreated.toDate().getTime(),
						};

						if (change.type === 'added' || change.type === 'modified') {
							const index = updated.findIndex((f) => f.id === id);
							if (index === -1) {
								updated.push(feature);
							} else {
								updated[index] = feature;
							}
						} else if (change.type === 'removed') {
							updated = updated.filter((f) => f.id !== id);
						}
					});

					return updated;
				});
			} catch (e) {
				console.error('Error getting aiWorkflows from Firebase', e);
				setIsError(true);
			} finally {
				setTimeout(() => {
					setIsFetching(false);
				}, 100);
				setIsLoading(false);
			}
		});

		return unsubscribe;
	}, [dealId]);

	const getWorkflows = (workflowType) => {
		let docs = workflows.slice();

		docs = docs.filter((doc) => doc.workflowType === workflowType);

		docs.sort((a, b) => b.dateCreatedMillis - a.dateCreatedMillis);
		return docs;
	};

	const getLatestWorkflow = (workflowType) => {
		const workflows = getWorkflows(workflowType);
		return workflows[0];
	};

	return {
		getWorkflows,
		getLatestWorkflow,
		isLoading,
		isFetching,
		isUninitialized,
		isError,
		isSuccess,
	};
};

export default useGetAIWorkflows;
